import React from 'react';

interface MultiColorIonoffProps {
  colors: string[]; // Array of hex colors for each segment
  className?: string;
  animated?: boolean; // Enable gentle rolling animation
  size?: 'small' | 'large'; // Font size: small for leaderboards, large for title
}

// Use EXACT chip background colors (when active/pressed)
// Logo colors match the active chip backgrounds for visual correspondence
const VIBRANT_COLOR_MAP: Record<string, string> = {
  '#FFE119': '#FFE119', // Yellow (same as active chip)
  '#3CB44B': '#3CB44B', // Green (same as active chip)
  '#FABED4': '#FABED4', // Pink (same as active chip)
  '#F58231': '#F58231', // Orange (same as active chip)
  '#4363D8': '#4363D8', // Blue (same as active chip)
  '#911EB4': '#911EB4', // Purple (same as active chip)
  '#F032E6': '#F032E6', // Magenta (same as active chip)
  '#42D4F4': '#42D4F4', // Cyan (same as active chip)
  '#469990': '#469990', // Teal (same as active chip)
  '#BFEF45': '#BFEF45', // Lime (same as active chip)
  '#E6194B': '#E6194B', // Red (same as active chip)
};

/**
 * Renders "ionoff" with colored letters on black/white background
 * Letters are colored, background adapts to light/dark mode
 * 
 * T=1: "ionoff" (all one color)
 * T=2: "i" | "onoff"
 * T=3: "i" | "on" | "off"
 * T=4: "i" | "on" | "o" | "ff"
 * T=5: "i" | "on" | "o" | "f" | "f"
 * T=6+: "i" | "o" | "n" | "o" | "f" | "f"
 */
export const MultiColorIonoff: React.FC<MultiColorIonoffProps> = ({
  colors,
  className = '',
  animated = false,
  size = 'small',
}) => {
  if (colors.length === 0) {
    // No colors provided - render plain
    return <span className={className}>ionoff</span>;
  }

  // Define segment patterns based on number of colors
  const getSegments = (numColors: number): Array<{ text: string; colorIndex: number }> => {
    if (numColors === 1) {
      return [{ text: 'ionoff', colorIndex: 0 }];
    } else if (numColors === 2) {
      return [
        { text: 'i', colorIndex: 0 },
        { text: 'onoff', colorIndex: 1 },
      ];
    } else if (numColors === 3) {
      return [
        { text: 'i', colorIndex: 0 },
        { text: 'on', colorIndex: 1 },
        { text: 'off', colorIndex: 2 },
      ];
    } else if (numColors === 4) {
      return [
        { text: 'i', colorIndex: 0 },
        { text: 'on', colorIndex: 1 },
        { text: 'o', colorIndex: 2 },
        { text: 'ff', colorIndex: 3 },
      ];
    } else if (numColors === 5) {
      return [
        { text: 'i', colorIndex: 0 },
        { text: 'on', colorIndex: 1 },
        { text: 'o', colorIndex: 2 },
        { text: 'f', colorIndex: 3 },
        { text: 'f', colorIndex: 4 },
      ];
    } else {
      // T >= 6: Each letter separate
      return [
        { text: 'i', colorIndex: 0 },
        { text: 'o', colorIndex: 1 },
        { text: 'n', colorIndex: 2 },
        { text: 'o', colorIndex: 3 },
        { text: 'f', colorIndex: 4 },
        { text: 'f', colorIndex: 5 },
      ];
    }
  };

  const segments = getSegments(colors.length);

  // Calculate animation delay for each segment (staggered)
  const getAnimationDelay = (index: number) => {
    return `${index * 0.15}s`; // 150ms stagger between each segment
  };

  // Size-specific styling
  const fontSize = size === 'large' ? '1.125rem' : '0.65rem'; // 18px for large (title), 10.4px for small (leaderboards)
  const gap = size === 'large' ? 'gap-1' : 'gap-0.5'; // Proportional gap
  
  return (
    <>
      {/* Inject keyframe animation for gentle rolling motion */}
      {animated && (
        <style>{`
          @keyframes gentleRoll {
            0%, 100% {
              transform: translateY(0) scale(1);
            }
            50% {
              transform: translateY(-3px) scale(1.05);
            }
          }
        `}</style>
      )}
      
      <span 
        className={`${className} inline-flex items-center ${gap}`}
        style={{
          fontFamily: "'Special Elite', serif",
          transform: size === 'large' ? 'translateY(-2px)' : 'none', // Slight vertical adjustment for title alignment
        }}
      >
        {segments.map((segment, index) => {
          const baseColor = colors[segment.colorIndex] || colors[0];
          const textColor = VIBRANT_COLOR_MAP[baseColor.toUpperCase()] || baseColor;
          
          return (
            <span
              key={index}
              className="inline-flex items-center justify-center"
              style={{
                backgroundColor: '#000000', // Black tile background
                color: textColor,
                fontWeight: 700, // Bold weight
                fontSize: fontSize,
                padding: size === 'large' ? '0.25rem 0.5rem' : '0.125rem 0.25rem', // Proportional minimal padding
                borderRadius: '2px',
                minWidth: size === 'large' ? '1.5rem' : '0.875rem', // Proportional minimal tile width
                letterSpacing: '0.01em',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)', // Subtle depth
                animation: animated ? `gentleRoll 2s ease-in-out infinite` : 'none',
                animationDelay: animated ? getAnimationDelay(index) : '0s',
              }}
            >
              {segment.text}
            </span>
          );
        })}
      </span>
    </>
  );
};

